// src/context/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { getUserSettings, updateUserSetting } from "../api/userSettings";

const ThemeContext = createContext();

// Setting key for theme in user_settings
const THEME_SETTING_KEY = "theme";

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState("light");
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // Get user from localStorage directly to avoid circular dependency
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserId(user?.user_id);
      } catch (e) {
        console.error("Error parsing user:", e);
      }
    }
  }, []);

  // Initial load - check backend first, then localStorage
  useEffect(() => {
    const loadTheme = async () => {
      try {
        // Try to get theme from backend if user is logged in
        if (userId) {
          const response = await getUserSettings();
          if (response.success && response.data && response.data[THEME_SETTING_KEY]) {
            const savedTheme = response.data[THEME_SETTING_KEY];
            setTheme(savedTheme);
            localStorage.setItem("theme", savedTheme);
            document.body.className = savedTheme === "dark" ? "dark-mode" : "";
            setIsLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error("Error loading theme from backend:", error);
      }

      // Fallback to localStorage
      const savedTheme = localStorage.getItem("theme") || "light";
      setTheme(savedTheme);
      document.body.className = savedTheme === "dark" ? "dark-mode" : "";
      setIsLoading(false);
    };

    loadTheme();
  }, [userId]);

  const toggleTheme = async () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.body.className = newTheme === "dark" ? "dark-mode" : "";

    // Save to backend if user is logged in
    if (userId) {
      try {
        await updateUserSetting(THEME_SETTING_KEY, newTheme);
      } catch (error) {
        console.error("Error saving theme to backend:", error);
      }
    }
  };

  const setThemeMode = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.body.className = newTheme === "dark" ? "dark-mode" : "";

    // Save to backend if user is logged in
    if (userId) {
      try {
        await updateUserSetting(THEME_SETTING_KEY, newTheme);
      } catch (error) {
        console.error("Error saving theme to backend:", error);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setThemeMode, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
